import React from 'react';
import PropTypes from 'prop-types';
import style from '@src/js/Style';
import css from '@src/styles/components/slider.scss';

const config = {

    itemsCount: 3,
    itemClass: css['slider__item'],
    navItemClass: css['slider-nav__item'],
    stateActive: css['active'],
};

export default class Slider extends React.Component {

    constructor(props) {
        
        super(props);

        this.activeStickId = 0;
        this.activeSlideId = 0;
        this.position = 0;

        /** refs */
        this.body = null;
        this.box = null;
        this.footer = null;
    }
    
    static get propTypes() {

        return {
            children: PropTypes.array,
            className: PropTypes.string,
            count: PropTypes.number,
            withoutGap: PropTypes.bool,
        };
    }

    render() {

        const items = this.getItems();
        if (items == undefined)
            return null;

        const classes = [];
        if (this.props.className)
            classes.push(this.props.className);

        const styles = {
            '--count': this.props.count ?? config.itemsCount,
        };
        if (this.props.withoutGap)
            styles['--gap'] = 0;

        return (
            <div className={ classes.join(' ') } style={ styles }>
                
                {/* slides */}
                <div ref={ (node) => { this.body = node; } } className={ style('slider__body', css) }>
                    <div ref={ (node) => { this.box = node; } } className={ style('slider__box', css) }>
                        { items.map((item, index) => this.renderSlide(item)) }
                    </div>
                </div>
                
                {/* footer navigation (paginator) */}
                { this.renderNavigation() }

            </div>
        );
    }

    isNavigationRequired() {

        const items = this.getItems();
        return items && items.length > config.itemsCount;
    }

    getItems() {

        return this.props.children;
    }

    componentDidMount() {

        const stateScrolling = style('scrolling', css);
        let
            scrolling = false,
            startPosition = 0,
            startCursorPosition = 0;

        const getCursorXPosition = (e) => {

            if (e.clientX != undefined)
                return e.clientX;

            const touch = e.touches[0] ?? null;
            return touch
                ? touch.clientX
                : null;
        };

        const stopDrag = () => {

            scrolling = false;
            this.body.classList.remove(stateScrolling);
        };

        /** drag start */
        const dragStartEvent = (e) => {

            if (e.isCan)
            e.preventDefault();

            startCursorPosition = getCursorXPosition(e);
            startPosition = this.position + startCursorPosition;
            scrolling = true;

            this.body.classList.add(stateScrolling);
        };

        this.body.addEventListener('mousedown', (e) => {

            e.button === 0
                ? dragStartEvent(e)
                : stopDrag();
        });
        this.body.addEventListener('touchstart', (e) => dragStartEvent(e), { passive: true });
        
        /** drag moving */
        const dragMovingEvent = (e) => {

            if (scrolling) {

                if (e.cancelable)
                    e.preventDefault();

                this.position = startPosition - getCursorXPosition(e);
                if (this.position < 0)
                    this.position = 0;

                const max = this.box.lastChild.offsetWidth + this.box.lastChild.offsetLeft - this.body.offsetWidth;
                if (this.position > max)
                    this.position = max;

                this.box.style.transform = `translateX(-${this.position}px)`;
            }
        };

        this.body.addEventListener('mousemove', (e) => dragMovingEvent(e));
        this.body.addEventListener('touchmove', (e) => dragMovingEvent(e));

        /** drag end */
        const dragEndEvent = (e) => {
            
            if (scrolling) {

                const cursorPosition = getCursorXPosition(e);

                if (cursorPosition != startCursorPosition) {

                    if (e.cancelable)
                        e.preventDefault();

                    stopDrag();

                    let activeSlide = null;
                    this.body
                        .querySelectorAll(`.${config.itemClass}`)
                        .forEach((el) => {

                            if (!activeSlide && el.offsetLeft >= this.position && el.dataset.id != undefined)
                                activeSlide = el;
                        });
                
                    if (!activeSlide)
                        activeSlide = this.box.lastChild;

                    this.setActiveSlideId(activeSlide.dataset.id);
                }
            }
        };
        
        this.body.addEventListener('click', (e) => dragEndEvent(e));
        // the click event is used instead of the mouseup
        // to prevent open the link after the slider has been moved

        this.body.addEventListener('touchend', (e) => dragEndEvent(e));
    }

    setActiveSlideId(id, target = null) {
        
        if (this.footer) {

            this.footer
                .querySelectorAll(`.${config.stateActive}`)
                .forEach((el) => el.classList.remove(config.stateActive));
            
            if (!target)
                target = this.footer.querySelector(`[data-id="${id}"]`);
        }
                
        if (target)
            target.classList.add(config.stateActive);
    }

    stickClickAction(e) {

        const
            { target } = e;
        
        /** fix footer */
        this.setActiveSlideId(null, target);
            
        const targetSlide = this.box
            .querySelector(`[data-id="${target.dataset.id}"]`);

        this.position = targetSlide.offsetLeft;

        const animationSpeed = 0.25;

        this.box.style.transition = `transform ${animationSpeed}s ease`;
        this.box.style.transform = `translateX(-${this.position}px)`;
        
        if (this.timeoutAnimation != undefined)
            clearTimeout(this.timeoutAnimation);

        this.timeoutAnimation = setTimeout(() => {

            this.box.style.transition = 'none';
            this.timeoutAnimation = null;

        }, animationSpeed * 1000 + 100);
        // clean animation after $animationSpeed seconds + 100ms
    }

    renderNavigation() {

        if (!this.isNavigationRequired())
            return null;
        // navigation is not required
        
        return (
            <div ref={ (node) => { this.footer = node; } } className={ style('slider-nav', css) }>
                { this.getItems().map((item, index) => this.renderSlideStick(index === 0)) }
            </div>
        );
    }

    renderSlideStick(isActive) {

        const id = this.activeStickId++;

        if (id != 0 && id % config.itemsCount != 0)
            return null;

        return <div
            key={ id }
            data-id={ id }
            onClick={ (e) => this.stickClickAction(e) }
            className={ `${config.navItemClass} ${isActive ? config.stateActive : ''}` }></div>;
    }

    renderSlide(item) {

        const
            id = this.activeSlideId++,
            classes = [ config.itemClass  ];

        if (this.props.count == 0)
            classes.push(css['slider__item--auto']);

        return (
            <div
                key={ id }
                data-id={ (id == 0 || id % config.itemsCount == 0) ? id : null }
                className={ classes.join(' ') }>
                    { item }
            </div>
        );
    }
}